import { compose, equals, filter, find, head, isEmpty, isNil, lensPath, pathOr, set } from 'ramda';
import {
  KIND_LOANAPP,
  KIND_LOANAPP_ROLE,
  LABELS,
  META_DATA,
  OBJECT_META,
  OWNER_REFERENCES,
  V1,
  YES_DOWNCASE
} from 'src/services/constants';
import { getNamespaceName } from 'src/services/objects/namespaces';
import { ObjectMeta, OwnerReference } from 'src/types';
import { isEmptyOrNil, isNotEmpty } from 'src/utilities/hasValue';
import { authenticatedUser, currentAppSession } from 'src/utilities/storage';

export const getLabelValue = (data: any, key: string) => {
  return pathOr('', [OBJECT_META, LABELS, key], data);
};

export const getMetadataValue = (data: any, key: string) => {
  return pathOr('', [META_DATA, key], data);
};

export const hasLabelValueEq = (key: string, value: string) => {
  return (data: any) => equals(getLabelValue(data, key), value)
}

export const setLabel = (key: string, value: string) => {
  return _setLabel(key, value)
};

const _setLabel = (key: string, value: string) => {
  if (isNotEmpty(key) && isNotEmpty(value)) {
    return (data: any) => set(lensPath([OBJECT_META, LABELS, key]), value, data);
  }
  return (data: any) => data;
}

export const mkObjectForLoanRole = (name: string, loanRole: string = '') => {
  const { loanAppId, loanRoleId, loanRoleAccountId } = currentAppSession();
  const owner_references: OwnerReference[] = [];
  const object: ObjectMeta = {
    name,
    account: loanRoleAccountId,
    namespace: getNamespaceName(),
    owner_references,
  };
  const { object_meta } = compose(
    setLabel(loanRole, YES_DOWNCASE),
    setOwnerReference(loanAppId, KIND_LOANAPP),
    setOwnerReference(loanRoleId, KIND_LOANAPP_ROLE)
  )({ object_meta: object });

  return object_meta;
};

export const mkObject = (name: string) => {
  const accountId = authenticatedUser.accountId.get();
  return mkObjectFor(name, `${accountId}`);
};
export const mkObjectFor = (name: any, account: any) => {
  const owner_references: OwnerReference[] = [];
  const object: ObjectMeta = {
    name,
    account,
    namespace: getNamespaceName(),
    owner_references,
  };
  return object;
};

export const getByOwnerReferenceId = (id: string, data: any) => {
  return head(
    filter((d: any) => {
      return !isEmpty(
        filter((o: any) => {
          return equals(o.uid, id);
        }, pathOr([], [OBJECT_META, OWNER_REFERENCES], d))
      );
    }, data)
  );
};

export const getByOwnerReferenceKind = (kind: string, data: any) => {
  return find((o: any) => {
    return equals(o.kind, kind);
  }, pathOr([], [OBJECT_META, OWNER_REFERENCES], data))
};

export const removeOwnerReference = (kind: string) => {
  if (isNil(kind) || isEmpty(kind)) {
    return (data: any) => data;
  }

  return (data: any) => {
    const refs = pathOr([], [OBJECT_META, OWNER_REFERENCES], data);
    return set(
      lensPath([OBJECT_META, OWNER_REFERENCES]),
      refs.filter((d: any) => !equals(d.kind, kind))
    )(data);
  };
};


export const getTypeMeta = (kind: string) => {
  return {
    kind: kind,
    api_version: V1,
  }
}

export const setOwnerReference = (id: string, kind: string) => {
  if (isEmptyOrNil(id) ||  isEmptyOrNil(kind)) {
    return (data: any) => data;
  }
  return (data: any) => {
    const ownerRef: any = pathOr([], [OBJECT_META, OWNER_REFERENCES], data);
    ownerRef.push({
      kind,
      api_version: V1,
      uid: `${id}`,
      name: `${id}`,
      block_owner_deletion: false,
    });
    return data;
  };
};
